/**
 * clientes-v2-store — Cliente novo (PT-BR, R-IMPORT 1/2).
 *
 * NÃO substitui `clients-store` legacy (que usa /api/v1/clients EN do schema R0).
 * Este store usa /api/v1/clientes (PT-BR, tabela `clientes` nova).
 *
 * R-IMPORT 4 vai consolidar as duas tabelas.
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type ClienteSource = 'manual' | 'agendor' | 'omie' | 'import';

export interface ClienteV2 {
  id: string;
  empresaId: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  cnpj: string | null;
  ie: string | null;
  im: string | null;
  enderecoLogradouro: string | null;
  enderecoNumero: string | null;
  enderecoBairro: string | null;
  enderecoCidade: string | null;
  enderecoUf: string | null;
  enderecoCep: string | null;
  segmentId: string | null;
  observacoesOperacionais: string | null;
  /** O4 (Orçamento v2): nº de unidades do condomínio (por unidade/mês). */
  numUnidades: number | null;
  email: string | null;
  telefone: string | null;
  omieId: string | null;
  /**
   * 🔴 Vinculado ao Omie, decidido no SERVIDOR pela tabela dona
   * (`omie_cliente_link`), não deduzido aqui da coluna legada `omieId`.
   *
   * `undefined` quando a consulta não perguntou — a LISTA sempre traz.
   */
  vinculadoOmie?: boolean;
  agendorId: string | null;
  omieUltimaSyncIso: string | null;
  source: ClienteSource;
  criadoPor: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  ativo: boolean;
  // Migration 360 (CRM ownership): vendedor dono do cliente (carteira).
  vendedorResponsavelId: string | null;
  // BUG-B1.6.2 Phase 2 (migration 146): default true (ON). Admin desliga
  // pra cliente que não quer email de "atendimento agendado" no Portal.
  notificarAgendamento: boolean;
  // ── Migration 393 (CL1.1) — superset do Omie ──────────────────────────
  cpf: string | null;
  /** Só dígitos do CNPJ ou CPF — chave-documento única (≠ do `cnpj` bruto). */
  documentoNormalizado: string | null;
  suframa: string | null;
  tipoAtividade: string | null;
  cnae: string | null;
  optanteSimples: boolean | null;
  produtorRural: boolean | null;
  contribuinteIcms: boolean | null;
  obsInternas: string | null;
  banco: string | null;
  agencia: string | null;
  conta: string | null;
  pix: string | null;
  titularDocumento: string | null;
  titularNome: string | null;
  parcelasPadrao: number | null;
  vendedorPadraoOmie: string | null;
  emailNfe: string | null;
  transportadora: string | null;
  gerarBoleto: boolean | null;
  codigoReceita: string | null;
  limiteCreditoCentavos: number | null;
  bloquearFaturamento: boolean | null;
  telefone2: string | null;
  // C-08 (h): `fax` saiu do domínio (schema, repo e tipos). A coluna continua
  // no banco com o legado do Omie, mas o v2 não lê mais — se voltar a aparecer
  // aqui, é regressão.
  website: string | null;
  entregaIgualFiscal: boolean;
  entregaLogradouro: string | null;
  entregaNumero: string | null;
  entregaBairro: string | null;
  entregaCidade: string | null;
  entregaUf: string | null;
  entregaCep: string | null;
  // Joined opcional
  segmentNome?: string;
  // CA2 (AJUSTES-POS): tags do cliente (multivalor) = o "Segmento" na lista.
  // Vem do backend na listagem (joinTags). Vazio/ausente = sem segmento.
  tags?: string[];
  /** 13/08: faltas do cadastro pela régua da travaGanho (chip "incompleto" da lista). */
  cadastroFaltas?: string[];
  // Mig 463 — campos de funcionário. O servidor sempre os mandou; o tipo é que
  // não os tinha (a ficha lia por `as Record`). RH-MENU (03/10) passou a ler a
  // data de desligamento pelo nome — entram aqui, opcionais (redação esconde
  // de quem não pode ver).
  cargoFuncionario?: string | null;
  dataAdmissao?: string | null;
  dataDemissao?: string | null;
  dataAniversario?: string | null;
  cltRegistro?: string | null;
  cboCodigo?: string | null;
}

export interface CreateClienteV2Input {
  razaoSocial: string;
  nomeFantasia?: string | null;
  cnpj?: string | null;
  segmentId?: string | null;
  email?: string | null;
  telefone?: string | null;
  enderecoLogradouro?: string | null;
  enderecoNumero?: string | null;
  enderecoBairro?: string | null;
  enderecoCidade?: string | null;
  enderecoUf?: string | null;
  enderecoCep?: string | null;
  observacoesOperacionais?: string | null;
  numUnidades?: number | null;
  // ── Migration 393 (CL1.1) — superset do Omie (todos opcionais) ─────────
  cpf?: string | null;
  suframa?: string | null;
  tipoAtividade?: string | null;
  cnae?: string | null;
  optanteSimples?: boolean | null;
  produtorRural?: boolean | null;
  contribuinteIcms?: boolean | null;
  obsInternas?: string | null;
  banco?: string | null;
  agencia?: string | null;
  conta?: string | null;
  pix?: string | null;
  titularDocumento?: string | null;
  titularNome?: string | null;
  parcelasPadrao?: number | null;
  vendedorPadraoOmie?: string | null;
  emailNfe?: string | null;
  transportadora?: string | null;
  gerarBoleto?: boolean | null;
  codigoReceita?: string | null;
  limiteCreditoCentavos?: number | null;
  bloquearFaturamento?: boolean | null;
  telefone2?: string | null;
  website?: string | null;
  entregaIgualFiscal?: boolean;
  entregaLogradouro?: string | null;
  entregaNumero?: string | null;
  entregaBairro?: string | null;
  entregaCidade?: string | null;
  entregaUf?: string | null;
  entregaCep?: string | null;
  // CL5 (estágio 1): sinal de carteira automática. O servidor decide de fato
  // pela flag de config e usa sempre o id do usuário logado.
  entrarNaCarteira?: boolean;
  /** 13/08: cadastro rápido do funil nasce com a etiqueta Cliente. */
  etiquetaCliente?: boolean;
  /** F4: etiquetas que o cadastro nasce tendo (wizard); lista fechada no servidor. */
  etiquetas?: string[];
  // F4 — campos por etiqueta aceitos no CREATE (gravados via updateCliente no servidor):
  cargoFuncionario?: string | null;
  dataAdmissao?: string | null;
  dataDemissao?: string | null;
  dataAniversario?: string | null;
  cltRegistro?: string | null;
  cboCodigo?: string | null;
  fornFazManutencao?: boolean;
  fornMinFaturarVendaCentavos?: number;
  fornMinFaturarManutencaoCentavos?: number;
  fornImpostoPct?: number;
  fornCobraImpostoRetirada?: boolean;
  fornPedeNfRetirada?: boolean;
  fornPedeNfAbatimento?: boolean;
  source?: 'manual' | 'agendor' | 'omie' | 'import';
}

export interface UpdateClienteV2Input extends Partial<CreateClienteV2Input> {
  ativo?: boolean;
  notificarAgendamento?: boolean;
}

// ── F2 (CLIENTES-FIX) — edição POR BLOCO na ficha do cliente ────────────────
// PATCH /api/v1/clientes/:id/bloco/:bloco — manda SÓ os campos que mudaram.
// CNPJ/CPF nunca entram aqui (o servidor devolve 422 se entrarem): documento
// muda só pelo fluxo próprio (correção/unificação de cadastros).
export type ClienteBlocoSlug =
  | 'principais'
  | 'fiscal'
  | 'bancarios'
  | 'faturamento'
  // C-08 (a) e (b): endereço fiscal e endereço de entrega passaram a ser
  // editáveis na ficha — antes só o wizard e o lookup de CNPJ escreviam.
  | 'endereco'
  | 'entrega'
  // Mig 463: bloco que só aparece com a etiqueta `Funcionário`. É o primeiro
  // bloco da ficha cuja EXISTÊNCIA depende de um dado do cadastro, e não da
  // permissão de quem olha.
  | 'funcionario'
  // Mig 465: aba própria, destravada pela etiqueta `Fornecedor`. Segundo bloco
  // cuja existência depende de um dado do cadastro, não da permissão de quem olha.
  | 'fornecedor';

export interface UpdateClienteBlocoInput {
  // bloco "principais"
  razaoSocial?: string;
  nomeFantasia?: string | null;
  telefone?: string | null;
  numUnidades?: number | null;
  // bloco "fiscal"
  ie?: string | null;
  im?: string | null;
  optanteSimples?: boolean | null;
  contribuinteIcms?: boolean | null;
  produtorRural?: boolean | null;
  suframa?: string | null;
  tipoAtividade?: string | null;
  cnae?: string | null;
  // bloco "bancarios"
  banco?: string | null;
  agencia?: string | null;
  conta?: string | null;
  pix?: string | null;
  titularNome?: string | null;
  titularDocumento?: string | null;
  // C-03 — bloco "faturamento": campos do Omie que já vinham no import e não
  // tinham tela nenhuma.
  telefone2?: string | null;
  website?: string | null;
  emailNfe?: string | null;
  transportadora?: string | null;
  parcelasPadrao?: number | null;
  vendedorPadraoOmie?: string | null;
  gerarBoleto?: boolean | null;
  codigoReceita?: string | null;
  /** Em CENTAVOS — a UI edita em reais (tipo de campo 'moeda'). */
  limiteCreditoCentavos?: number | null;
  bloquearFaturamento?: boolean | null;
  // C-08 (a) — bloco "endereco" (fiscal).
  enderecoLogradouro?: string | null;
  enderecoNumero?: string | null;
  enderecoBairro?: string | null;
  enderecoCidade?: string | null;
  enderecoUf?: string | null;
  enderecoCep?: string | null;
  // C-08 (b) — bloco "entrega".
  entregaIgualFiscal?: boolean;
  entregaLogradouro?: string | null;
  entregaNumero?: string | null;
  entregaBairro?: string | null;
  entregaCidade?: string | null;
  entregaUf?: string | null;
  entregaCep?: string | null;
  // Mig 463 — bloco "funcionario". A ficha só mostra estes campos quando o
  // cadastro tem a etiqueta `Funcionário`; ficam no tipo sempre porque a
  // etiqueta é um dado que entra e sai, não um subtipo do cadastro.
  /** 'YYYY-MM-DD'. */
  dataAniversario?: string | null;
  cltRegistro?: string | null;
  cboCodigo?: string | null;
  /** 'YYYY-MM-DD'. */
  dataAdmissao?: string | null;
  /** 'YYYY-MM-DD'. ⚠️ Preenchida ≠ cadastro inativo — são duas dimensões. */
  dataDemissao?: string | null;
  /** Cargo da pessoa NA IGM. ≠ o "Papel" do contato (síndico, administradora). */
  cargoFuncionario?: string | null;
  // Mig 465 — bloco "fornecedor": regras de faturamento que ESTE fornecedor
  // aplica à IGM. Sem `| null`: as colunas são NOT NULL DEFAULT 0, e zero é
  // "fatura a partir de qualquer valor" — uma decisão, não uma ausência.
  fornFazManutencao?: boolean;
  fornMinFaturarVendaCentavos?: number;
  fornMinFaturarManutencaoCentavos?: number;
  /** Percentual: 12.5 é 12,5%. */
  fornImpostoPct?: number;
  fornCobraImpostoRetirada?: boolean;
  fornPedeNfRetirada?: boolean;
  fornPedeNfAbatimento?: boolean;
}

// ============================================================
// CL1.1 — Tabelas filhas do cliente: e-mails, tags, observações.
// ============================================================

export interface ClienteEmailV2 {
  id: string;
  clienteId: string;
  email: string;
  principal: boolean;
  criadoEmIso: string;
}
// ⛔ Mig 803 (decisão A, 28/10): as marcações saíram do e-mail da empresa e moram no contato
// (`responsavel-cliente-store.ts`, `definirMarcacoes`).
export interface ClienteTagV2 {
  id: string;
  clienteId: string;
  tag: string;
  criadoEmIso: string;
}
export interface ClienteObservacaoV2 {
  id: string;
  clienteId: string;
  texto: string;
  autorId: string | null;
  criadoEmIso: string;
  editadoEmIso: string | null;
}

export function useClienteEmails(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'emails', clienteId ?? ''] as const,
    queryFn: () => api.get<ClienteEmailV2[]>(`/api/v1/clientes/${encodeURIComponent(clienteId!)}/emails`),
    enabled: hasToken() && !!clienteId,
  });
}

/**
 * Histórico salarial (mig 463) — 🔴 só admin. O servidor devolve 403 a quem não
 * é; este hook não repete a checagem de propósito. Uma segunda cópia da regra no
 * front seria a que dá certo por engano quando a do servidor quebrar — e a que
 * vale é a do servidor.
 */
export interface LancamentoSalarialV2 {
  id: string;
  clienteId: string;
  /** 'YYYY-MM-DD' — desde quando o valor vale (≠ quando foi digitado). */
  vigenciaEmIso: string;
  valorCentavos: number;
  observacao: string | null;
  autorId: string | null;
  autorNome: string | null;
  criadoEmIso: string;
}

export function useSalarios(clienteId: string | null | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'salarios', clienteId ?? ''] as const,
    queryFn: () =>
      api.get<{ items: LancamentoSalarialV2[] }>(
        `/api/v1/clientes/${encodeURIComponent(clienteId!)}/salarios`,
      ),
    enabled: hasToken() && !!clienteId && habilitado,
  });
}

export function useLancarSalario(clienteId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { vigenciaEmIso: string; valorCentavos: number; observacao?: string | null }) =>
      api.post<LancamentoSalarialV2>(
        `/api/v1/clientes/${encodeURIComponent(clienteId!)}/salarios`,
        body,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['v1', 'clientes', 'salarios', clienteId ?? ''] }),
  });
}

/**
 * Mig 465 — o que o fornecedor fornece, e o catálogo de tipos do Compras.
 *
 * O catálogo vem da rota do Compras (a fonte é uma só, `compra_tipo_item`); o
 * que muda é só o vínculo, que agora pode apontar para um cadastro de
 * `clientes`. Duplicar o catálogo aqui seria criar um segundo motor para uma
 * lista que já existe.
 */
export interface TipoItemV2 {
  id: string;
  nome: string;
}

export function useClienteTiposItem(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'tipos-item', clienteId ?? ''] as const,
    queryFn: () =>
      api.get<{ items: TipoItemV2[] }>(
        `/api/v1/clientes/${encodeURIComponent(clienteId!)}/tipos-item`,
      ),
    enabled: hasToken() && !!clienteId,
  });
}

export function useComprasTiposItem() {
  return useQuery({
    queryKey: ['v1', 'compras', 'tipos-item'] as const,
    queryFn: () => api.get<{ items: TipoItemV2[] }>('/api/v1/compras/tipos-item'),
    enabled: hasToken(),
  });
}

export function useSalvarClienteTiposItem(clienteId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tipoItemIds: string[]) =>
      api.put<{ ok: true }>(`/api/v1/clientes/${encodeURIComponent(clienteId!)}/tipos-item`, {
        tipoItemIds,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['v1', 'clientes', 'tipos-item', clienteId ?? ''] }),
  });
}

/**
 * Crédito do fornecedor (mig 467) — leitura.
 *
 * Reusa `GET /compras/fornecedores/:id/credito`, que já existia: desde a mig
 * 466 o id do fornecedor É o id do cadastro, então a ficha chama a rota do
 * Compras com o próprio id e não precisa de endpoint novo.
 *
 * Só leitura porque não há escrita: o crédito NASCE do faturamento direto de uma
 * cotação, não de alguém digitando um valor. Um formulário aqui inventaria uma
 * porta de entrada que o processo não tem.
 */
export interface CreditoFornecedorV2 {
  fornecedorId: string;
  saldoCentavos: number;
  movimentos: Array<{
    id: string;
    tipo: 'credito' | 'debito';
    valorCentavos: number;
    descricao: string | null;
    criadoEmIso: string;
  }>;
}

export function useCreditoFornecedor(clienteId: string | null | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: ['v1', 'compras', 'credito', clienteId ?? ''] as const,
    queryFn: () =>
      api.get<CreditoFornecedorV2>(
        `/api/v1/compras/fornecedores/${encodeURIComponent(clienteId!)}/credito`,
      ),
    enabled: hasToken() && !!clienteId && habilitado,
  });
}

export function useClienteTags(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'tags', clienteId ?? ''] as const,
    queryFn: () => api.get<ClienteTagV2[]>(`/api/v1/clientes/${encodeURIComponent(clienteId!)}/tags`),
    enabled: hasToken() && !!clienteId,
  });
}

/**
 * P3 (AJUSTES-POS-2): valores DISTINTOS de segmento (tags) já existentes na
 * base — vêm do Omie. Alimenta o seletor read-only de Segmento na ficha:
 * ninguém digita segmento novo no v2, só escolhe entre os que já existem.
 */
export function useSegmentosClientesDisponiveis() {
  return useQuery({
    queryKey: ['v1', 'clientes', 'segmentos-disponiveis'] as const,
    queryFn: () => api.get<string[]>('/api/v1/clientes/segmentos'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useClienteObservacoes(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'observacoes', clienteId ?? ''] as const,
    queryFn: () =>
      api.get<ClienteObservacaoV2[]>(`/api/v1/clientes/${encodeURIComponent(clienteId!)}/observacoes`),
    enabled: hasToken() && !!clienteId,
  });
}

// ── CL1.2 — Histórico de alterações (trilha de→para, só leitura) ────────────
export interface ClienteHistoricoV2 {
  id: string;
  clienteId: string;
  /** Rótulo PT-BR do campo alterado (ex.: "Razão social"). */
  campo: string;
  valorAnterior: string | null;
  valorNovo: string | null;
  autorId: string | null;
  criadoEmIso: string;
}

export function useClienteHistorico(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'historico', clienteId ?? ''] as const,
    queryFn: () =>
      api.get<ClienteHistoricoV2[]>(`/api/v1/clientes/${encodeURIComponent(clienteId!)}/historico`),
    enabled: hasToken() && !!clienteId,
  });
}

/** Ações das tabelas filhas (add/remove e-mail·tag; add/editar/excluir observação). */
export function useClienteFilhasActions(clienteId: string) {
  const qc = useQueryClient();
  const base = `/api/v1/clientes/${encodeURIComponent(clienteId)}`;
  const invalidate = (sub: 'emails' | 'tags' | 'observacoes') =>
    qc.invalidateQueries({ queryKey: ['v1', 'clientes', sub, clienteId] });

  const addEmail = useMutation({
    mutationFn: (input: { email: string; principal?: boolean }) =>
      api.post<ClienteEmailV2>(`${base}/emails`, input),
    onSuccess: () => invalidate('emails'),
  });
  // R-1 (B-08): a tela fazia DELETE + POST pra trocar a estrela. Se o POST
  // falhasse, o e-mail SUMIA do cadastro. Uma chamada só, transacional no servidor.
  const definirEmailPrincipal = useMutation({
    mutationFn: (emailId: string) =>
      api.patch<{ ok: true }>(`${base}/emails/${emailId}/principal`),
    onSuccess: () => invalidate('emails'),
  });
  const removeEmail = useMutation({
    mutationFn: (emailId: string) => api.delete<{ ok: true }>(`${base}/emails/${emailId}`),
    onSuccess: () => invalidate('emails'),
  });
  const addTag = useMutation({
    mutationFn: (tag: string) => api.post<ClienteTagV2>(`${base}/tags`, { tag }),
    onSuccess: () => invalidate('tags'),
  });
  const removeTag = useMutation({
    mutationFn: (tagId: string) => api.delete<{ ok: true }>(`${base}/tags/${tagId}`),
    onSuccess: () => invalidate('tags'),
  });
  const addObservacao = useMutation({
    mutationFn: (texto: string) => api.post<ClienteObservacaoV2>(`${base}/observacoes`, { texto }),
    onSuccess: () => invalidate('observacoes'),
  });
  const updateObservacao = useMutation({
    mutationFn: ({ obsId, texto }: { obsId: string; texto: string }) =>
      api.patch<ClienteObservacaoV2>(`${base}/observacoes/${obsId}`, { texto }),
    onSuccess: () => invalidate('observacoes'),
  });
  const removeObservacao = useMutation({
    mutationFn: (obsId: string) => api.delete<{ ok: true }>(`${base}/observacoes/${obsId}`),
    onSuccess: () => invalidate('observacoes'),
  });

  return { addEmail, definirEmailPrincipal, removeEmail, addTag, removeTag, addObservacao, updateObservacao, removeObservacao };
}

/** O valor de "sem etiqueta nenhuma" — o mesmo que o servidor entende. */
export const SEM_ETIQUETA = '__sem__';

export function useClientesV2(opts: {
  includeInactive?: boolean;
  search?: string;
  limit?: number;
  /**
   * Recorte por etiqueta, resolvido no SERVIDOR.
   *
   * Era filtro em memória sobre a página de 300, com 761 no banco: filtrar
   * "Fornecedor" trazia os fornecedores das 300 primeiras linhas, não os 305,
   * e a tela não dizia que mostrava um pedaço. Mesmo defeito que a BUSCA
   * desta tela já teve, e a mesma cura.
   */
  etiqueta?: string;
  /**
   * Início da página. **Não é preferência**: reseta a cada busca ou troca de
   * filtro, senão a pessoa volta na página 4 de um recorte que agora tem 2.
   */
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  if (opts.includeInactive) params.set('includeInactive', 'true');
  if (opts.search) params.set('search', opts.search);
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.etiqueta) params.set('etiqueta', opts.etiqueta);
  if (opts.offset) params.set('offset', String(opts.offset));
  const qs = params.toString();

  return useQuery({
    queryKey: [
      'v1',
      'clientes',
      opts.includeInactive ?? false,
      // O filtro `empresaContratanteId` saiu em 01/08 com o conceito de cliente
      // terceirizado. Ele ocupava uma posição desta chave de cache; a posição
      // some junto — chave menor invalida o cache de quem estiver com a página
      // aberta na hora do deploy, o que é o comportamento correto (os dados
      // mudaram de forma).
      opts.search ?? '',
      // L3: o `limit` faltava na chave — mudar o tamanho da página devolvia o
      // cache antigo, silenciosamente.
      opts.limit ?? 0,
      opts.etiqueta ?? '',
    ] as const,
    queryFn: () =>
      api.get<{ items: ClienteV2[]; total: number; limit: number; offset: number }>(
        `/api/v1/clientes${qs ? `?${qs}` : ''}`,
      ),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/**
 * L3 (LAYOUT-CANONICO) — contagem REAL do banco, não da página carregada.
 *
 * A lista carrega no máximo `limit` registros; o cabeçalho precisa dizer a
 * verdade sobre o total mesmo assim. Ver `GET /api/v1/clientes/contagem`.
 */
/**
 * O total que ESTA pessoa vê COM ESTES filtros.
 *
 * Recebe os mesmos parâmetros da lista de propósito: o servidor lê os dois do
 * mesmo motor, e mandar menos daqui faria o total responder sobre um universo
 * e a lista sobre outro — que era o defeito (dizia 761 para quem via 754, e
 * dizia 761 também com um filtro mostrando 305).
 */
export function useClientesContagem(opts: {
  includeInactive?: boolean;
  search?: string;
  etiqueta?: string;
} = {}) {
  const p = new URLSearchParams();
  if (opts.includeInactive) p.set('includeInactive', 'true');
  if (opts.search) p.set('search', opts.search);
  if (opts.etiqueta) p.set('etiqueta', opts.etiqueta);
  const qs = p.toString() ? `?${p.toString()}` : '';
  return useQuery({
    queryKey: [
      'v1',
      'clientes',
      'contagem',
      opts.includeInactive ?? false,
      opts.search ?? '',
      opts.etiqueta ?? '',
    ] as const,
    queryFn: () =>
      api.get<{ total: number; vinculadosOmie: number }>(`/api/v1/clientes/contagem${qs}`),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

// ─── S-2 (SEGURANCA-ACESSO-2) — lookup mínimo pra seletor ─────────────────

/**
 * O par id + nome que um combobox precisa, e nada além disso.
 *
 * `GET /api/v1/clientes` (lista completa) passou a exigir a chave
 * `clientes.ver`. Quem só escolhe cliente num formulário — a Solicitação de
 * Compra é o caso — não recebeu essa chave e usa `/clientes/lookup`, que só
 * pede login. Não troque estes hooks por `useClientesV2` "porque devolve mais
 * campos": é justamente o que quebraria Compras e reabriria o vazamento da base.
 */
export interface ClienteLookupItem {
  id: string;
  nome: string;
}

export function useClientesLookup(opts: { search?: string; limit?: number; soClientes?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set('q', opts.search);
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.soClientes) params.set('soClientes', '1');
  const qs = params.toString();
  return useQuery({
    queryKey: ['v1', 'clientes', 'lookup', opts.search ?? '', opts.limit ?? 0, opts.soClientes ? 'so-clientes' : 'todos'] as const,
    queryFn: () =>
      api.get<{ items: ClienteLookupItem[] }>(`/api/v1/clientes/lookup${qs ? `?${qs}` : ''}`),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/** O nome do cliente JÁ escolhido — sem abrir a ficha completa em `/:id`. */
export function useClienteLookupItem(id: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'lookup', 'id', id ?? ''] as const,
    queryFn: () =>
      api.get<{ items: ClienteLookupItem[] }>(
        `/api/v1/clientes/lookup?id=${encodeURIComponent(id!)}`,
      ),
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
}

/** Single cliente V2 — pra forms de edit. BUG-B1.6.2: precisa pra
 *  ler notificarAgendamento no toggle do PortalClienteTab. */
export function useClienteV2(id: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'single', id ?? ''] as const,
    queryFn: () => api.get<ClienteV2>(`/api/v1/clientes/${encodeURIComponent(id!)}`),
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
}

export function useClienteV2Actions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['v1', 'clientes'] });

  const create = useMutation({
    mutationFn: (input: CreateClienteV2Input) =>
      api.post<ClienteV2>('/api/v1/clientes', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClienteV2Input }) =>
      api.put<ClienteV2>(`/api/v1/clientes/${id}`, input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/clientes/${id}`),
    onSuccess: invalidate,
  });
  // CL1.3 — fusão guiada: unifica dois cadastros (vencedor absorve o perdedor).
  const fundir = useMutation({
    mutationFn: ({ vencedorId, perdedorId }: { vencedorId: string; perdedorId: string }) =>
      api.post<ClienteV2>(
        `/api/v1/clientes/${encodeURIComponent(vencedorId)}/fundir/${encodeURIComponent(perdedorId)}`,
      ),
    onSuccess: invalidate,
  });
  // F2 — edição por bloco (Dados principais / Fiscal / Bancários) na ficha.
  // O invalidate de prefixo ['v1','clientes'] também renova o histórico
  // (trilha) e o single — a aba Histórico mostra a alteração na hora.
  const updateBloco = useMutation({
    mutationFn: ({
      id,
      bloco,
      input,
    }: {
      id: string;
      bloco: ClienteBlocoSlug;
      input: UpdateClienteBlocoInput;
    }) =>
      api.patch<ClienteV2>(
        `/api/v1/clientes/${encodeURIComponent(id)}/bloco/${bloco}`,
        input,
      ),
    onSuccess: invalidate,
  });

  return { create, update, remove, fundir, updateBloco };
}

// ── CL1.3 — Lookup de CNPJ (BrasilAPI via backend, read-only) ───────────────
export interface CnpjLookupResult {
  cnpj: string;
  razaoSocial: string | null;
  nomeFantasia: string | null;
  logradouro: string | null;
  numero: string | null;
  bairro: string | null;
  municipio: string | null;
  uf: string | null;
  cep: string | null;
  cnaeCodigo: string | null;
  cnaeDescricao: string | null;
  cnae: string | null;
  telefone: string | null;
  email: string | null;
}

/**
 * Consulta os dados públicos de um CNPJ (autopreenchimento). NÃO É OMIE — bate
 * na BrasilAPI via backend. Falha da API vira ApiError 502 (mensagem pt-BR) que
 * o componente mostra sem derrubar o formulário.
 */
export function useLookupCnpj() {
  return useMutation({
    mutationFn: (cnpj: string) =>
      api.get<CnpjLookupResult>(`/api/v1/clientes/lookup-cnpj/${encodeURIComponent(cnpj)}`),
  });
}

// ── C-08 (d) — Lookup de CEP (ViaCEP via backend, read-only) ────────────────
export interface CepLookupResult {
  cep: string;
  logradouro: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
}

/**
 * Endereço a partir do CEP. A consulta é feita pelo BACKEND (nunca direto do
 * browser: CORS, rate-limit por IP do usuário e zero cache compartilhado).
 * Erro vira ApiError com mensagem pt-BR — 422 formato, 404 inexistente,
 * 502 provedor fora do ar.
 */
export function useLookupCep() {
  return useMutation({
    mutationFn: (cep: string) =>
      api.get<CepLookupResult>(`/api/v1/clientes/lookup-cep/${encodeURIComponent(cep)}`),
  });
}

// ── CL3 — Cofre de acessos e senhas ─────────────────────────────────────────
// SEGURANÇA: a senha vem SEMPRE mascarada (`••••••`) na listagem. Só o endpoint
// de revelar (que audita) devolve o texto em claro, e só na resposta dele.
export type CofreFerramenta = 'anydesk' | 'teamviewer' | 'outro';

export interface CofreAcessoRemoto {
  id: string;
  clienteId: string;
  nomePc: string;
  ferramenta: CofreFerramenta;
  idAcesso: string | null;
  usuario: string | null;
  observacao: string | null;
  /** Sempre `••••••` — nunca o valor real. */
  senha: string;
  criadoPor: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}
export interface CofreSenhaEquip {
  id: string;
  clienteId: string;
  equipamento: string;
  usuario: string | null;
  isPadrao: boolean;
  observacao: string | null;
  /** Sempre `••••••` — nunca o valor real. */
  senha: string;
  criadoPor: string | null;
  criadoEmIso: string;
  /** O equipamento do coletor a que esta senha se pendura (mig 529). */
  equipamentoId?: string | null;
  /** O IP vem do coletor — somente leitura do lado das senhas. */
  equipamentoEndereco?: string | null;
  /**
   * 🔴 O equipamento saiu do monitoramento e a senha FICOU, marcada.
   * Apagar senha sozinho e irreversivel, e ninguem quer descobrir depois.
   */
  equipamentoSumiu?: boolean;
}
export interface CofreResposta {
  acessosRemotos: CofreAcessoRemoto[];
  senhasEquip: CofreSenhaEquip[];
}
export interface CofreAuditoriaItem {
  id: string;
  autorNome: string;
  acaoTecnica: string;
  acao: 'mostrar' | 'copiar' | null;
  tipo: 'acesso' | 'equip' | null;
  quando: string;
}

export interface AcessoRemotoInput {
  nomePc: string;
  ferramenta: CofreFerramenta;
  idAcesso?: string | null;
  /** Na EDICAO, ausente = mantem a senha guardada (10/09). Obrigatoria ao criar. */
  senha?: string;
  usuario?: string | null;
  observacao?: string | null;
}
export interface SenhaEquipInput {
  equipamento: string;
  usuario?: string | null;
  /** Na EDICAO, ausente = mantem a senha guardada (10/09). Obrigatoria ao criar. */
  senha?: string;
  isPadrao?: boolean;
  observacao?: string | null;
  /** Vazio = senha digitada a mao, para cliente sem coletor. */
  equipamentoId?: string | null;
}

/** Os equipamentos monitorados que ainda NAO tem senha — o que o dropdown lista. */
export interface EquipamentoSemSenha {
  id: string;
  nome: string;
  endereco: string;
  silenciado: number;
}

export function useEquipamentosSemSenha(clienteId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['cofre', 'equipamentos-sem-senha', clienteId ?? ''],
    queryFn: () =>
      api.get<{ itens: EquipamentoSemSenha[] }>(
        `/api/v1/clientes/${encodeURIComponent(clienteId!)}/cofre/equipamentos-sem-senha`,
      ),
    enabled: !!clienteId && enabled,
    retry: false,
  });
}

export function useCofre(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'cofre', clienteId ?? ''] as const,
    queryFn: () => api.get<CofreResposta>(`/api/v1/clientes/${encodeURIComponent(clienteId!)}/cofre`),
    enabled: hasToken() && !!clienteId,
    // CA4: 403 por segmento é determinístico — não adianta retentar.
    retry: false,
  });
}

export function useCofreAuditoria(clienteId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'cofre', 'auditoria', clienteId ?? ''] as const,
    queryFn: () =>
      api.get<{ items: CofreAuditoriaItem[] }>(
        `/api/v1/clientes/${encodeURIComponent(clienteId!)}/cofre/auditoria`,
      ),
    enabled: hasToken() && !!clienteId && enabled,
  });
}

/** Revela (decifra) a senha de um item — só aqui o texto em claro trafega. Audita. */
export function useRevelarSenha(clienteId: string) {
  return useMutation({
    mutationFn: ({
      tipo,
      itemId,
      acao,
    }: {
      tipo: 'acesso' | 'equip';
      itemId: string;
      acao: 'mostrar' | 'copiar';
    }) =>
      api.post<{ senha: string }>(
        `/api/v1/clientes/${encodeURIComponent(clienteId)}/cofre/${tipo}/${encodeURIComponent(itemId)}/revelar`,
        { acao },
      ),
  });
}

export function useCofreActions(clienteId: string) {
  const qc = useQueryClient();
  const base = `/api/v1/clientes/${encodeURIComponent(clienteId)}/cofre`;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['v1', 'clientes', 'cofre', clienteId] });

  const addAcesso = useMutation({
    mutationFn: (input: AcessoRemotoInput) => api.post<{ id: string }>(`${base}/acessos`, input),
    onSuccess: invalidate,
  });
  const updateAcesso = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: AcessoRemotoInput }) =>
      api.put<{ ok: true }>(`${base}/acessos/${encodeURIComponent(itemId)}`, input),
    onSuccess: invalidate,
  });
  const removeAcesso = useMutation({
    mutationFn: (itemId: string) => api.delete<{ ok: true }>(`${base}/acessos/${encodeURIComponent(itemId)}`),
    onSuccess: invalidate,
  });
  const addEquip = useMutation({
    mutationFn: (input: SenhaEquipInput) => api.post<{ id: string }>(`${base}/equip`, input),
    onSuccess: invalidate,
  });
  const updateEquip = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: SenhaEquipInput }) =>
      api.put<{ ok: true }>(`${base}/equip/${encodeURIComponent(itemId)}`, input),
    onSuccess: invalidate,
  });
  const removeEquip = useMutation({
    mutationFn: (itemId: string) => api.delete<{ ok: true }>(`${base}/equip/${encodeURIComponent(itemId)}`),
    onSuccess: invalidate,
  });

  return { addAcesso, updateAcesso, removeAcesso, addEquip, updateEquip, removeEquip };
}
